import {
  apiActivityActivityGetActivityCommodity,
  ApiActivityActivityGetActivityCommodityResponse,
  apiActivityVirtualActivityFindVirtualActivityDetailVo,
  ApiActivityVirtualActivityFindVirtualActivityDetailVoResponse,
  apiCommoditySkuStockWriteOffCommodityStock,
  apiCommodityVirtualExchangeGetSkuStock,
  ApiCommodityVirtualExchangeGetSkuStockRequest,
  apiCommodityWriteOffCommodityDetail,
  ApiCommodityWriteOffCommodityDetailResponse,
  apiCommodityWriteOffCommodityMarketingDetail,
  ApiCommodityWriteOffCommodityMarketingDetailResponse,
  apiEvaluationEvaluateGetLatestEvaluateList,
  ApiEvaluationEvaluateGetLatestEvaluateListResponse,
} from '@/api'
import { AsyncReturnType, RequiredDeep } from 'vtils/types'
import {
  createModelPlus,
  getSeckillInfo,
  prepareData,
  requiredDeep,
  SeckillInfo,
  submit,
} from '@/utils'
import { defaultIndexTo, ii, keyBy, min } from 'vtils'
import { defineProvider } from '@/hooks'
import { Ref } from '@vue/composition-api'

export interface RawBookGoodsModel
  extends RequiredDeep<ApiCommodityWriteOffCommodityDetailResponse> {
  /** 商品ID */
  goodsId: string

  /** 是否已下架 */
  isOff: boolean

  /** 店铺是否休息中 */
  isRest: boolean

  /** 是否已停业 */
  isClosed: boolean

  /** 是否兑换商品 */
  isExchange: boolean

  /** 是否代金券 */
  isCoupon: boolean

  /** 是否日期库存 */
  isDateStock: boolean

  /** 是否售罄 */
  isSold: boolean

  /** 是否多彩豆商品 */
  isDcd: boolean

  /** 多彩豆最低数量 */
  dcdMinCount: number

  /** 评价数量 */
  commentCount: number

  /** 评价星级 */
  commentStar: number

  /** 评价列表 */
  commentList: RequiredDeep<ApiEvaluationEvaluateGetLatestEvaluateListResponse>['list']

  /** 营销数据 */
  marketingData: RequiredDeep<ApiCommodityWriteOffCommodityMarketingDetailResponse>
  /** 商品虚拟活动信息 */
  virtualActivityData: RequiredDeep<ApiActivityVirtualActivityFindVirtualActivityDetailVoResponse>

  /** 选中的 SKU 索引 */
  skuIndex: number

  /** SKU 列表 */
  skuList: Array<
    RequiredDeep<ApiCommodityWriteOffCommodityDetailResponse>['skuList'][0] & {
      /** 拼团价格 */
      groupBuyingPrice: number
      /** 需多彩豆数量 */
      dcdCount: number
    }
  >

  /** 秒杀数据 */
  seckillData: RequiredDeep<ApiActivityActivityGetActivityCommodityResponse>['activityData']

  /** 秒杀信息 */
  seckillInfo: SeckillInfo

  /** 是否有秒杀商品状态 */
  hasSeckill: boolean

  /** 是否秒杀商品 */
  isSeckill: boolean

  /** 是否拼团商品 */
  isGroupBuying: boolean

  noShare: boolean

  /** 分享参数 */
  shareParams: any

  /** 虚拟商品sku信息 */
  virtualSkuInfo: {
    /** 总评论 */
    cutStock: number
    /** 好评率(99%) */
    skuId: string
  }

  /** 刷新秒杀 */
  refreshSeckill: () => Promise<void>

  refreshMarketing: () => Promise<void>

  /** 获取商品虚拟活动信息 */
  findVirtualActivityDetailVo: () => Promise<void>
}

export type BookGoodsModel = AsyncReturnType<typeof getBookGoodsModel>

export async function getBookGoodsModel(
  goodsId: string,
  parentCommodityId?: string,
  skuId?: string,
  isExchange?: boolean,
  previewStatus?: any,
) {
  const { goodsData } = await prepareData({
    goodsData: apiCommodityWriteOffCommodityDetail(
      {
        commodityId: goodsId,
        previewStatus,
      },
      {
        noErrorToast: true,
      },
    )
      .then(requiredDeep)
      .catch(
        () => ({} as RequiredDeep<ApiCommodityWriteOffCommodityDetailResponse>),
      ),
  })

  if (!goodsData.commodityId) {
    return createModelPlus<RawBookGoodsModel>({
      isOff: true,
    } as any)
  }

  const skuIndex = defaultIndexTo(
    goodsData.skuList.findIndex(item => !!item.defaultShow),
    0,
  )

  const { commentData, marketingData, stockData } = await prepareData({
    commentData: apiEvaluationEvaluateGetLatestEvaluateList({
      commodityServiceId: goodsId,
      pageIndex: 1,
      pageSize: 3,
    }).then(requiredDeep),
    marketingData: apiCommodityWriteOffCommodityMarketingDetail({
      commodityId: goodsId,
      skuId: goodsData.skuList[skuIndex].skuId,
    }).then(requiredDeep),
    // .then(res => {
    //   res.discounts = [
    //     {
    //       activityId: 1,
    //       describe: '该商品支持工会卡积分抵扣支付',
    //     },
    //     {
    //       activityId: 13,
    //       describe: '该商品支持工会卡积分抵扣支付',
    //     },
    //   ]
    //   res.channelIntegrals = [
    //     {
    //       activityId: 1,
    //       describe: '该商品支持工会卡积分抵扣支付',
    //     },
    //     {
    //       activityId: 13,
    //       describe: '该商品支持工会卡积分抵扣支付',
    //     },
    //   ]
    //   return res
    // })
    stockData: apiCommoditySkuStockWriteOffCommodityStock({
      commodityId: goodsId,
    }).then(requiredDeep),
  })

  const bookGoodsModel = createModelPlus<RawBookGoodsModel>({
    ...{},
    ...goodsData,
    virtualActivityData: {} as any,
    skuIndex: skuIndex,
    skuList: goodsData.skuList.map(item => ({
      ...item,
      groupBuyingPrice: 0,
      dcdCount: 0,
    })),
    isExchange: false,
    get goodsId() {
      return goodsId
    },
    get isOff() {
      return false
    },
    get isRest() {
      return this.store.storeStatus === 4
    },
    get isClosed() {
      return this.store.storeStatus === 3
    },
    get isCoupon() {
      return +this.commodityClassifyType === 1
    },
    get isDateStock() {
      return +this.stockType === 3
    },
    get isSold() {
      return stockData.soldOutFlag
    },
    get isDcd() {
      return !!this.marketingData.integrals?.length && !this.isSeckill
    },
    get dcdMinCount() {
      return this.isDcd
        ? min(this.marketingData.integrals.map(sku => sku.integral))!
        : 0
    },
    get commentCount() {
      return commentData.totalNum
    },
    get commentStar() {
      return commentData.evaluateStat
    },
    get commentList() {
      return commentData.list
    },
    marketingData: marketingData,

    seckillData: {} as any,
    seckillInfo: {} as any,
    virtualSkuInfo: {} as any,
    get hasSeckill() {
      return !!this.seckillData.activityId
    },
    get isSeckill() {
      return (
        this.hasSeckill && getSeckillInfo(this.seckillData as any).isRunning
      )
    },
    get isGroupBuying() {
      return !!this.marketingData.groupBuying?.hasActivity
    },
    get noShare() {
      return !!this.supportIntegral
    },
    get shareParams() {
      return {
        title: this.commodityTitle,
        desc: this.commoditySubTitle,
        cover: this.coverPic,
      }
    },
    async refreshSeckill() {
      await submit(async $ => {
        $.start()
        const {
          // 获取秒杀数据
          seckillData,
        } = await prepareData({
          seckillData: apiActivityActivityGetActivityCommodity({
            commodityId: goodsId,
          }),
        })
        this.seckillData = ((seckillData.hasActivity &&
          seckillData.activityData) ||
          {}) as any
        this.skuList = this.isSeckill
          ? seckillData.activityData!.commodityInfo!.skuInfo!.map(
              (item, index) =>
                ({
                  skuId: item.skuId!,
                  commodityStock: item.stock!,
                  stockType: 0,
                  skuPic: item.pic!,
                  sellPrice: item.price!,
                  specValue: item.skuDesc!,
                  defaultShow: index === 0,
                } as any),
            )
          : goodsData.skuList
      })
    },
    async refreshMarketing() {
      this.marketingData = await apiCommodityWriteOffCommodityMarketingDetail({
        commodityId: goodsId,
        skuId: this.skuList[this.skuIndex].skuId,
      }).then(requiredDeep)
    },
    // 获取商品虚拟活动信息
    async findVirtualActivityDetailVo() {
      await submit(async $ => {
        $.start()
        const {
          // 获取营销数据
          virtualActivityData,
        } = await prepareData({
          virtualActivityData:
            apiActivityVirtualActivityFindVirtualActivityDetailVo({
              commodityId: goodsId,
            }),
        })
        this.virtualActivityData = virtualActivityData as any
      })
    },
  })

  bookGoodsModel.$watch(
    () => bookGoodsModel.skuIndex,
    async () => {
      const { marketingData } = await prepareData({
        marketingData: apiCommodityWriteOffCommodityMarketingDetail({
          commodityId: goodsId,
          skuId: bookGoodsModel.skuList[bookGoodsModel.skuIndex].skuId,
        }).then(requiredDeep),
      })
      bookGoodsModel.$update({
        marketingData,
      })
    },
  )

  bookGoodsModel.findVirtualActivityDetailVo()

  const {
    // 获取秒杀数据
    seckillData,
  } = await prepareData({
    seckillData: apiActivityActivityGetActivityCommodity({
      commodityId: goodsId,
    }),
  })

  const preOrderRequestData: ApiCommodityVirtualExchangeGetSkuStockRequest = {
    commodityId: goodsId,
    parentCommodityId: parentCommodityId,
    skuId: skuId,
    commodityType: '1',
  }
  if (isExchange) {
    bookGoodsModel.isExchange = true
    const virtualSkuInfo = await apiCommodityVirtualExchangeGetSkuStock(
      preOrderRequestData,
    )

    bookGoodsModel.virtualSkuInfo = {
      cutStock: virtualSkuInfo.cutStock || 0,
      skuId: virtualSkuInfo.skuId || '',
    }
  }

  bookGoodsModel.seckillData = ((seckillData.hasActivity &&
    seckillData.activityData) ||
    {}) as any
  bookGoodsModel.skuList = bookGoodsModel.isSeckill
    ? seckillData.activityData!.commodityInfo!.skuInfo!.map(
        (item, index) =>
          ({
            skuId: item.skuId!,
            commodityStock: item.stock!,
            stockType: 0,
            skuPic: item.pic!,
            sellPrice: item.price!,
            specValue: item.skuDesc!,
            defaultShow: index === 0,
          } as any),
      )
    : bookGoodsModel.isDcd
    ? ii((): typeof bookGoodsModel.skuList => {
        const dcdSkuMap = keyBy(
          bookGoodsModel.marketingData.integrals,
          item => item.skuId,
        )
        return goodsData.skuList
          .filter(sku => !!dcdSkuMap[sku.skuId])
          .map(sku => ({
            ...sku,
            groupBuyingPrice: 0,
            dcdCount: dcdSkuMap[sku.skuId].integral,
            commodityStock: dcdSkuMap[sku.skuId].stock,
            sellPrice: dcdSkuMap[sku.skuId].discountPrice,
          }))
      })
    : goodsData.skuList

  await bookGoodsModel.$watchResolveOnFirstTrigger(
    () => bookGoodsModel.marketingData.groupBuying,
    () => {
      if (bookGoodsModel.marketingData.groupBuying?.skuInfos?.length) {
        bookGoodsModel.marketingData.groupBuying.skuInfos.forEach(sku => {
          const foundSku = bookGoodsModel.skuList.find(
            x => x.skuId === sku.skuId,
          )
          if (foundSku && !foundSku.groupBuyingPrice) {
            foundSku.groupBuyingPrice = sku.maxPrice
          }
        })
      }
    },
    {
      immediate: true,
    },
  )

  // 是秒杀商品，倒计时
  if (bookGoodsModel.hasSeckill) {
    if (bookGoodsModel.isSeckill) {
      bookGoodsModel.skuIndex = 0
    }
    const { dispose } = getSeckillInfo(
      bookGoodsModel.seckillData as any,
      info => {
        bookGoodsModel.seckillInfo = info
        if (info.isEnded) {
          dispose?.()
          bookGoodsModel.refreshSeckill()
        }
      },
    )
    bookGoodsModel.$beforeDestroy(() => dispose?.())
  }

  return bookGoodsModel
}

export const bookGoodsModelProvider = defineProvider<Ref<BookGoodsModel>>()
